import { z } from 'zod';
import { IsoDateCodec, NullableIsoDateCodec } from './date-codecs.js';
import { UuidSchema } from './primitives/uuid.js';
import { CursorPaginationSchema, PaginationOffsetHasMoreSchema } from './shared/pagination.js';

const VERIFIED_TYPES = ['none', 'blue', 'business', 'government'] as const; // Keep in sync with src/lib/twitter/verification.ts.

export const DmConversationsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(20),
  cursor: z.coerce.date().optional(),
});

export const DmConversationMessagesQuerySchema = z.object({
  limit: z.coerce.number().int().min(10).max(100).default(50),
  cursor: z.coerce.date().optional(),
});

export const DmParticipantSummarySchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  username: z.string().nullable(),
  profileImageUrl: z.string().nullable(),
  verifiedType: z.enum(VERIFIED_TYPES).nullable(),
});

export const DmConversationSummarySchema = z.object({
  id: UuidSchema,
  externalId: z.string(),
  ownerProfileId: z.string(),
  participantProfileIds: z.array(z.string()),
  title: z.string(),
  subtitle: z.string().nullable(),
  lastEventAt: NullableIsoDateCodec,
  lastMessagePreview: z.string().nullable(),
  isFullMode: z.boolean(),
});

const DmConversationsPaginationSchema = CursorPaginationSchema.extend({
  cursor: NullableIsoDateCodec,
  limit: z.number().int().min(1).max(50),
  totalCount: z.number().int().min(0),
});

export const DmConversationsResponseSchema = z.object({
  conversations: z.array(DmConversationSummarySchema),
  participants: z.record(z.string(), DmParticipantSummarySchema),
  pagination: DmConversationsPaginationSchema,
});

export const DmMessageAttachmentSchema = z.object({
  mediaKey: z.string(),
  mediaType: z.string(),
  metadata: z.record(z.string(), z.unknown()),
});

export const DmMessageReactionSchema = z.object({
  senderProfileId: z.string(),
  key: z.string().nullable(),
  eventId: z.string().nullable(),
  createdAt: NullableIsoDateCodec,
});

export const DmMessageUrlEntitySchema = z.object({
  url: z.string(),
  expandedUrl: z.string().nullable(),
  displayUrl: z.string().nullable(),
});

export const DmMessageSummarySchema = z.object({
  id: UuidSchema,
  conversationId: UuidSchema,
  occurredAt: IsoDateCodec,
  senderProfileId: z.string(),
  direction: z.enum(['inbound', 'outbound']),
  text: z.string().nullable(),
  hasPayload: z.boolean(),
  attachments: z.array(DmMessageAttachmentSchema),
  reactions: z.array(DmMessageReactionSchema),
  urlEntities: z.array(DmMessageUrlEntitySchema),
});

const DmConversationMessagesPaginationSchema = PaginationOffsetHasMoreSchema.extend({
  cursor: NullableIsoDateCodec,
  limit: PaginationOffsetHasMoreSchema.shape.limit.min(1).max(100),
  offset: PaginationOffsetHasMoreSchema.shape.offset.min(0),
});

export const DmConversationMessagesResponseSchema = z.object({
  conversation: DmConversationSummarySchema,
  participants: z.record(z.string(), DmParticipantSummarySchema),
  messages: z.array(DmMessageSummarySchema),
  pagination: DmConversationMessagesPaginationSchema,
  hasFullContent: z.boolean(),
});

export type DmConversationsQuery = z.infer<typeof DmConversationsQuerySchema>;
export type DmConversationsResponse = z.infer<typeof DmConversationsResponseSchema>;
export type DmConversationSummary = z.infer<typeof DmConversationSummarySchema>;
export type DmParticipantSummary = z.infer<typeof DmParticipantSummarySchema>;
export type DmConversationMessagesQuery = z.infer<typeof DmConversationMessagesQuerySchema>;
export type DmConversationMessagesResponse = z.infer<typeof DmConversationMessagesResponseSchema>;
export type DmMessageSummary = z.infer<typeof DmMessageSummarySchema>;
